import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Eye, History, Loader2 } from "lucide-react";
import {
  CanonicalReviewDecisionDialog,
  type CanonicalReviewDecision,
} from "@/components/compliance-review/CanonicalReviewDecisionDialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type {
  CanonicalRelationshipReviewDetail,
  CanonicalRelationshipReviewDocumentDetail,
  CanonicalRelationshipReviewDocumentHistory,
} from "@/data/contractor/compliance/canonicalRelationshipReview";
import {
  CANONICAL_INTERNAL_REVIEW_READ_ERROR_MESSAGES,
  CanonicalInternalReviewReadClientError,
} from "@/services/canonical-internal-review-read.shared";
import {
  createRelationshipReviewOpsReviewReadClient,
} from "@/services/relationship-review-ops-review.service";

type RelationshipReviewClient = ReturnType<
  typeof createRelationshipReviewOpsReviewReadClient
>;

type ExactDocumentIdentity = Readonly<{
  reviewId: string;
  relationshipRequirementId: string;
  documentId: string;
  requirementLabel: string;
}>;

function errorMessage(error: unknown) {
  const kind = error instanceof CanonicalInternalReviewReadClientError
    ? error.kind
    : "unexpected_error";
  return CANONICAL_INTERNAL_REVIEW_READ_ERROR_MESSAGES[kind];
}

function safeDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function safeBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function statusLabel(value: string): string {
  const normalized = value.replace(/_/g, " ");
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function documentDecisionLabel(value: string): string {
  return value === "pending" ? "Pending review" : statusLabel(value);
}

export function CanonicalRelationshipDocumentReviewPanel({
  organizationId,
  parentContractorId,
  relationshipDetail,
  relationshipLabel,
  client,
  onDocumentDecisionCommitted,
}: Readonly<{
  organizationId: string;
  parentContractorId: string;
  relationshipDetail: CanonicalRelationshipReviewDetail;
  relationshipLabel: string;
  client: RelationshipReviewClient;
  onDocumentDecisionCommitted: () => Promise<void>;
}>) {
  const generation = useRef(0);
  const [selectedIdentity, setSelectedIdentity] =
    useState<ExactDocumentIdentity | null>(null);
  const [documentDetail, setDocumentDetail] =
    useState<CanonicalRelationshipReviewDocumentDetail | null>(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [documentError, setDocumentError] = useState<unknown>(null);
  const [history, setHistory] =
    useState<CanonicalRelationshipReviewDocumentHistory | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<unknown>(null);
  const [preview, setPreview] = useState<Readonly<{
    signedUrl: string;
    expiresAt: string;
    fileName: string;
    mimeType: string;
    byteSize: number;
  }> | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const [decision, setDecision] =
    useState<CanonicalReviewDecision | null>(null);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    generation.current += 1;
    setSelectedIdentity(null);
    setDocumentDetail(null);
    setDocumentError(null);
    setHistory(null);
    setHistoryError(null);
    setPreview(null);
    setPreviewError(null);
    setDecision(null);
    setSuccessMessage("");
    return () => {
      generation.current += 1;
    };
  }, [
    organizationId,
    parentContractorId,
    relationshipDetail.reviewId,
  ]);

  useEffect(() => {
    if (!preview) return;
    const remaining = new Date(preview.expiresAt).getTime() - Date.now();
    if (remaining <= 0) {
      setPreview(null);
      return;
    }
    const timeout = window.setTimeout(() => setPreview(null), remaining);
    return () => window.clearTimeout(timeout);
  }, [preview]);

  const documentsByType = useMemo(() => {
    const grouped = new Map<
      string,
      CanonicalRelationshipReviewDetail["documents"][number][]
    >();
    for (const document of relationshipDetail.documents) {
      const documents = grouped.get(document.documentType) ?? [];
      documents.push(document);
      grouped.set(document.documentType, documents);
    }
    return grouped;
  }, [relationshipDetail.documents]);

  async function loadDocument(
    identity: ExactDocumentIdentity,
    preserveDecisionDraft = false,
  ): Promise<CanonicalRelationshipReviewDocumentDetail | null> {
    if (documentLoading) return null;
    const requestGeneration = ++generation.current;
    setSelectedIdentity(identity);
    setDocumentLoading(true);
    setDocumentError(null);
    setHistory(null);
    setHistoryError(null);
    setPreview(null);
    setPreviewError(null);
    if (!preserveDecisionDraft) setDecision(null);
    try {
      const result = await client.getDocumentDetail({
        organizationId,
        parentContractorId,
        reviewId: identity.reviewId,
        relationshipRequirementId: identity.relationshipRequirementId,
        documentId: identity.documentId,
      });
      if (generation.current !== requestGeneration) return null;
      setDocumentDetail(result);
      return result;
    } catch (error) {
      if (generation.current === requestGeneration) {
        setDocumentDetail(null);
        setDocumentError(error);
      }
      return null;
    } finally {
      if (generation.current === requestGeneration) setDocumentLoading(false);
    }
  }

  async function loadDocumentHistory(
    identity: ExactDocumentIdentity,
  ): Promise<void> {
    if (historyLoading) return;
    const requestGeneration = ++generation.current;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const result = await client.getDocumentHistory({
        organizationId,
        parentContractorId,
        reviewId: identity.reviewId,
        relationshipRequirementId: identity.relationshipRequirementId,
        documentId: identity.documentId,
      });
      if (generation.current === requestGeneration) setHistory(result);
    } catch (error) {
      if (generation.current === requestGeneration) {
        setHistory(null);
        setHistoryError(error);
      }
    } finally {
      if (generation.current === requestGeneration) setHistoryLoading(false);
    }
  }

  async function openPreview(): Promise<void> {
    if (!selectedIdentity || !documentDetail || previewLoading) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      setPreview(await client.getDocumentPreview({
        organizationId,
        parentContractorId,
        reviewId: selectedIdentity.reviewId,
        relationshipRequirementId:
          selectedIdentity.relationshipRequirementId,
        documentId: selectedIdentity.documentId,
        expiresInSeconds: 300,
      }));
    } catch (error) {
      setPreview(null);
      setPreviewError(error);
    } finally {
      setPreviewLoading(false);
    }
  }

  async function submitDocumentDecision(input: Readonly<{
    reasonCode?: string;
    note?: string;
    idempotencyKey: string;
  }>): Promise<void> {
    if (!selectedIdentity || !documentDetail || !decision) return;
    const identity = selectedIdentity;
    try {
      await client.submitDocumentDecision({
        organizationId,
        parentContractorId,
        reviewId: identity.reviewId,
        relationshipRequirementId: identity.relationshipRequirementId,
        documentId: identity.documentId,
        decision,
        ...input,
        expectedLatestDecisionId: documentDetail.latestDecisionId,
      });
      setDecision(null);
      setSuccessMessage("Relationship document decision recorded.");
      await onDocumentDecisionCommitted();
      await loadDocument(identity);
      await loadDocumentHistory(identity);
    } catch (error) {
      if (
        error instanceof CanonicalInternalReviewReadClientError
        && (
          error.kind === "stale_decision"
          || error.kind === "idempotency_conflict"
        )
      ) {
        await onDocumentDecisionCommitted();
        await loadDocument(identity, true);
        await loadDocumentHistory(identity);
      }
      throw error;
    }
  }

  const reviewableDocument = Boolean(
    selectedIdentity
    && documentDetail
    && documentDetail.documentStatus === "active",
  );

  return (
    <div className="mt-3 space-y-3">
      <div>
        <p className="text-sm font-medium">Relationship documents</p>
        <p className="text-xs text-muted-foreground">
          Each decision applies only to the exact current document for its
          relationship requirement.
        </p>
      </div>

      <div className="grid gap-2 md:grid-cols-2">
        {relationshipDetail.requirements.map((requirement) => {
          const matches = documentsByType.get(requirement.documentType) ?? [];
          const document = matches.length === 1 ? matches[0] : null;
          return (
            <div key={requirement.id} className="rounded border p-3 text-xs">
              <p className="font-medium">{requirement.label}</p>
              <p className="text-muted-foreground">
                {requirement.required ? "Required" : "Optional"} ·{" "}
                {statusLabel(requirement.documentType)}
              </p>
              {matches.length === 0 ? (
                <p className="mt-2 text-muted-foreground">
                  No current relationship document.
                </p>
              ) : null}
              {matches.length > 1 ? (
                <Alert variant="destructive" className="mt-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Exact document unavailable</AlertTitle>
                  <AlertDescription>
                    Multiple documents match this requirement. Review actions
                    are disabled until the Website runtime identifies one
                    current document.
                  </AlertDescription>
                </Alert>
              ) : null}
              {document ? (
                <div className="mt-2 space-y-2">
                  <p className="text-muted-foreground">
                    {document.fileName} · {statusLabel(document.status)} ·{" "}
                    {safeDate(document.createdAt)}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={documentLoading}
                    onClick={() => void loadDocument({
                      reviewId: relationshipDetail.reviewId,
                      relationshipRequirementId: requirement.id,
                      documentId: document.id,
                      requirementLabel: requirement.label,
                    })}
                  >
                    Review exact document
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {documentLoading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading exact document review…
        </p>
      ) : null}
      {documentError ? (
        <Alert variant="destructive" role="alert">
          <AlertTitle>{errorMessage(documentError).title}</AlertTitle>
          <AlertDescription>
            {errorMessage(documentError).description}
          </AlertDescription>
        </Alert>
      ) : null}

      {selectedIdentity && documentDetail ? (
        <div
          className="rounded-md border p-3"
          aria-label="Relationship document review detail"
        >
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h4 className="font-semibold">
                {selectedIdentity.requirementLabel}
              </h4>
              <p className="text-xs text-muted-foreground">
                {documentDetail.fileName}
              </p>
            </div>
            <Badge variant="outline">
              {documentDecisionLabel(documentDetail.currentDecision)}
            </Badge>
          </div>

          <dl className="mt-3 grid gap-2 text-xs md:grid-cols-2">
            <div>
              <dt className="font-medium">Requirement</dt>
              <dd className="text-muted-foreground">
                {selectedIdentity.requirementLabel}
              </dd>
            </div>
            <div>
              <dt className="font-medium">Document review</dt>
              <dd className="text-muted-foreground">
                {documentDecisionLabel(documentDetail.currentDecision)}
              </dd>
            </div>
            <div>
              <dt className="font-medium">Workflow effect</dt>
              <dd className="text-muted-foreground">
                {documentDetail.blocker
                  ? "Blocking relationship completion"
                  : "Satisfied"}
              </dd>
            </div>
            <div>
              <dt className="font-medium">Submitted</dt>
              <dd className="text-muted-foreground">
                {safeDate(documentDetail.submittedAt)}
              </dd>
            </div>
            <div>
              <dt className="font-medium">File</dt>
              <dd className="text-muted-foreground">
                {documentDetail.mimeType} · {safeBytes(documentDetail.byteSize)}
              </dd>
            </div>
            <div>
              <dt className="font-medium">Issue date</dt>
              <dd className="text-muted-foreground">
                {safeDate(documentDetail.issueDate)}
              </dd>
            </div>
            <div>
              <dt className="font-medium">Expiration date</dt>
              <dd className="text-muted-foreground">
                {safeDate(documentDetail.expiresAt)}
              </dd>
            </div>
            <div>
              <dt className="font-medium">Document status</dt>
              <dd className="text-muted-foreground">
                {statusLabel(documentDetail.documentStatus)}
              </dd>
            </div>
            <div>
              <dt className="font-medium">Document decision</dt>
              <dd className="text-muted-foreground">
                {documentDecisionLabel(documentDetail.currentDecision)}
              </dd>
            </div>
          </dl>
          {documentDetail.note ? (
            <div className="mt-3 text-xs">
              <p className="font-medium">Submission note</p>
              <p className="whitespace-pre-wrap text-muted-foreground">
                {documentDetail.note}
              </p>
            </div>
          ) : null}
          {documentDetail.blocker ? (
            <Alert className="mt-3">
              <AlertTitle>Document prerequisite</AlertTitle>
              <AlertDescription>
                {statusLabel(documentDetail.blocker)}
              </AlertDescription>
            </Alert>
          ) : null}

          <div
            className="mt-3 flex flex-wrap gap-2"
            aria-label="Relationship document actions"
          >
            <Button
              size="sm"
              variant="outline"
              disabled={!documentDetail.canPreview || previewLoading}
              onClick={() => void openPreview()}
            >
              {previewLoading
                ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                : <Eye className="mr-1 h-4 w-4" />}
              Preview
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={historyLoading}
              onClick={() => void loadDocumentHistory(selectedIdentity)}
            >
              <History className="mr-1 h-4 w-4" />
              History
            </Button>
            <Button
              size="sm"
              disabled={!reviewableDocument}
              onClick={() => setDecision("approved")}
            >
              Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={!reviewableDocument}
              onClick={() => setDecision("rejected")}
            >
              Reject
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!reviewableDocument}
              onClick={() => setDecision("changes_requested")}
            >
              Request Changes
            </Button>
          </div>
        </div>
      ) : null}

      {previewError ? (
        <Alert variant="destructive" role="alert">
          <AlertTitle>{errorMessage(previewError).title}</AlertTitle>
          <AlertDescription>
            {errorMessage(previewError).description}
          </AlertDescription>
        </Alert>
      ) : null}
      {historyError ? (
        <Alert variant="destructive" role="alert">
          <AlertTitle>{errorMessage(historyError).title}</AlertTitle>
          <AlertDescription>
            {errorMessage(historyError).description}
          </AlertDescription>
        </Alert>
      ) : null}
      {successMessage ? (
        <Alert aria-live="polite">
          <AlertTitle>Document decision recorded</AlertTitle>
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      ) : null}

      {history ? (
        <div
          className="rounded-md border p-3"
          aria-label="Relationship document decision history"
        >
          <h4 className="font-semibold">Immutable document history</h4>
          {history.decisions.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">
              No document decisions recorded.
            </p>
          ) : (
            <div className="mt-2 space-y-2">
              {history.decisions.map((item) => (
                <div key={item.decisionId} className="rounded border p-2 text-xs">
                  <p className="font-medium">{statusLabel(item.decision)}</p>
                  <p className="text-muted-foreground">
                    {safeDate(item.decidedAt)} · {item.reviewerRole}
                  </p>
                  {item.reasonCode ? (
                    <p>Reason: {statusLabel(item.reasonCode)}</p>
                  ) : null}
                  {item.note ? (
                    <p className="whitespace-pre-wrap">{item.note}</p>
                  ) : null}
                  {item.previousDecisionId ? (
                    <p className="text-muted-foreground">
                      Supersedes the previous immutable decision.
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      <Dialog
        open={preview !== null}
        onOpenChange={(open) => !open && setPreview(null)}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Relationship document preview</DialogTitle>
            <DialogDescription>
              Preview {preview?.fileName ?? "the selected private document"}.
              Previewing does not approve it.
            </DialogDescription>
          </DialogHeader>
          {preview ? (
            <iframe
              title="Relationship document private preview"
              src={preview.signedUrl}
              className="h-[65vh] w-full rounded border"
            />
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>
              Close preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CanonicalReviewDecisionDialog
        decision={decision}
        targetLabel={`${relationshipLabel} · ${
          selectedIdentity?.requirementLabel ?? "selected document"
        }`}
        onClose={() => setDecision(null)}
        onConfirm={submitDocumentDecision}
      />
    </div>
  );
}
